/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga';
import { Button } from '../../shared/ui';
import Modal from '../../components/modal/Modal';
import { buildClientIdQrPayload, getClientId } from '../../hookServices/clientId';
import { resolveScannedQrPayload, scanQrPayloadFromImageFile } from '../../hookServices/processQrScan';
import styles from './ClientIdQrCard.module.css';

export default function ClientIdQrCard({ clientId, inModal = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isImportingFromPhoto, setIsImportingFromPhoto] = useState(false);
  const [photoImportError, setPhotoImportError] = useState(null);
  const [energyCardInstallOpen, setEnergyCardInstallOpen] = useState(false);

  const effectiveClientId = clientId || getClientId();
  const qrPayload = useMemo(() => buildClientIdQrPayload(effectiveClientId), [effectiveClientId]);

  const applyResolvedQr = result => {
    if (result.type === 'redirect') {
      window.location.href = result.href;
      return;
    }
    if (result.type === 'navigate') {
      navigate(result.path);
      return;
    }
    if (result.type === 'energyCardAlreadyScanned') {
      setPhotoImportError(t('qrScanner.energyCardAlreadyScanned'));
      return;
    }
    if (result.type === 'energyCardRequiresNativeApp') {
      setEnergyCardInstallOpen(true);
      return;
    }
    setPhotoImportError(t(result.messageKey || 'qrScanner.loadFromPhotoError'));
  };

  const handleEnergyCardInstallApp = () => {
    ReactGA.event({
      category: 'installApp',
      action: 'history photo energy card web install popup',
    });
    setEnergyCardInstallOpen(false);
    navigate('/install-app');
  };

  const handleQrScreenshotChange = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    setPhotoImportError(null);
    setIsImportingFromPhoto(true);

    try {
      const payload = await scanQrPayloadFromImageFile(file);
      if (!payload) {
        setPhotoImportError(t('historyPage.loadClientIdQrFromPhotoError'));
        return;
      }
      const result = await resolveScannedQrPayload(payload);
      applyResolvedQr(result);
    } catch {
      setPhotoImportError(t('historyPage.loadClientIdQrFromPhotoError'));
    } finally {
      setIsImportingFromPhoto(false);
    }
  };

  useEffect(() => {
    let active = true;

    async function generateQr() {
      if (!qrPayload) {
        if (active) {
          setQrDataUrl('');
          setIsGenerating(false);
        }
        return;
      }

      if (active) {
        setIsGenerating(true);
      }

      try {
        const url = await QRCode.toDataURL(qrPayload, {
          margin: 1,
          width: 280,
          errorCorrectionLevel: 'M',
          color: {
            dark: '#241631',
            light: '#fff9ff',
          },
        });

        if (active) {
          setQrDataUrl(url);
        }
      } catch {
        if (active) {
          setQrDataUrl('');
        }
      } finally {
        if (active) {
          setIsGenerating(false);
        }
      }
    }

    generateQr();
    return () => {
      active = false;
    };
  }, [qrPayload]);

  const installModal = energyCardInstallOpen ? (
    <Modal setModalOpen={setEnergyCardInstallOpen} modalOpen={energyCardInstallOpen}>
      <div style={{ position: 'relative', textAlign: 'center', padding: '8px 4px 4px' }}>
        <button
          type="button"
          aria-label={t('close')}
          onClick={() => setEnergyCardInstallOpen(false)}
          style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            width: '48px',
            height: '48px',
            border: 'none',
            background: 'transparent',
            color: '#fff',
            fontSize: '40px',
            fontWeight: 300,
            lineHeight: 1,
            cursor: 'pointer',
            padding: 0,
            zIndex: 1,
          }}
        >
          ×
        </button>
        <p
          style={{
            fontSize: '18px',
            margin: '12px 0 24px',
            color: 'rgba(255, 255, 255, 0.85)',
            lineHeight: '1.5',
            padding: '0 40px',
          }}
        >
          {t('qrScanner.energyCardWebInstallPopup')}
        </p>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Button
            onClick={handleEnergyCardInstallApp}
            style={{
              display: 'flex',
              width: '100%',
              maxWidth: '100%',
              margin: 0,
              justifyContent: 'center',
            }}
          >
            <span style={{ fontSize: '22px', letterSpacing: '1px' }}>{t('installApp')}</span>
          </Button>
          <Button
            variant="outline-alt"
            onClick={() => setEnergyCardInstallOpen(false)}
            style={{
              display: 'flex',
              width: '100%',
              maxWidth: '100%',
              margin: 0,
              justifyContent: 'center',
            }}
          >
            <span style={{ fontSize: '22px', letterSpacing: '1px' }}>{t('close')}</span>
          </Button>
        </div>
      </div>
    </Modal>
  ) : null;

  const content = (
    <div className={styles.inner}>
      <div className={styles.title}>{t('historyPage.clientIdQrTitle')}</div>
      {t('historyPage.clientIdQrSubtitle') ? (
        <div className={styles.subtitle}>{t('historyPage.clientIdQrSubtitle')}</div>
      ) : null}

      {qrDataUrl ? (
        <div className={styles.qrFrame}>
          <img src={qrDataUrl} alt={t('historyPage.clientIdQrAlt')} className={styles.qrImage} />
        </div>
      ) : (
        <div className={styles.placeholder}>
          {isGenerating || qrPayload ? t('historyPage.loading') : t('historyPage.clientUiId')}
        </div>
      )}

      <Button className={`historyPageButton ${styles.scanButton}`} onClick={() => navigate('/scanner')}>
        <span className={styles.scanButtonLabel}>{t('historyPage.scanClientIdQr')}</span>
      </Button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleQrScreenshotChange}
        className={styles.hiddenFileInput}
        aria-hidden="true"
        tabIndex={-1}
      />
      <Button
        variant="outline"
        className={styles.loadScreenshotButton}
        onClick={() => fileInputRef.current?.click()}
        disabled={isImportingFromPhoto}
      >
        <span className={styles.scanButtonLabel}>
          {isImportingFromPhoto ? t('historyPage.loading') : t('historyPage.loadClientIdQrFromPhoto')}
        </span>
      </Button>
      {photoImportError ? <p className={styles.photoImportError}>{photoImportError}</p> : null}
      {installModal}
    </div>
  );

  if (inModal) {
    return content;
  }

  return <section className={`historyQrPanel ${styles.panel}`}>{content}</section>;
}
